import { VARIANCE_ACTIONS, varianceActionTargetFile } from './varianceActions.js';
import { adjustedDpeTotalForStatus } from './dpeActionPreviewTotals.js';

const toMoneyNumber = (value, fallback = 0) => {
  if (value === null || value === undefined || value === '') return fallback;
  const number = Number(value);
  return Number.isFinite(number) ? Math.round(number * 100) / 100 : fallback;
};

const changed = (left, right) => Math.abs(toMoneyNumber(left) - toMoneyNumber(right)) >= 0.005;
const PERSISTED_STATUSES = new Set(['saved', 'exported', 'resolved']);

const actionTargetFile = (action = {}) => {
  const explicitTarget = action.targetSource || action.targetFile || varianceActionTargetFile(action.actionType);
  if (explicitTarget === 'gm' || explicitTarget === 'cpq') return explicitTarget;
  if (action.source === 'gm' || action.source === 'cpq') return action.source;
  if (action.sourceFile === 'gm' || action.sourceFile === 'cpq') return action.sourceFile;
  const sourceLabel = String(action.sourceLabel || '').toLowerCase();
  if (sourceLabel.includes('gm file')) return 'gm';
  if (sourceLabel.includes('cpq file')) return 'cpq';
  if (action.exportLane === 'gm_correction') return 'gm';
  if (action.exportLane === 'cpq_peoplesoft_adjustment') return 'cpq';
  return null;
};

const rowKeyCandidate = (source = {}) => (
  source?.sourceRowKey
  || source?.targetRowKey
  || source?.rowKey
  || source?.fileRowId
  || source?.storageKey
  || source?.id
  || source?.rowNumber
  || ''
);

const booleanValue = (value) => {
  if (value === true || value === false) return value;
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'true') return true;
  if (normalized === 'false') return false;
  return null;
};

const cpqRowKey = (row = {}) => String(
  row.id
  || row.rowKey
  || row.row_key
  || row.fileRowId
  || row.storageKey
  || row.rowNumber
  || ''
).trim().toLowerCase();

const cpqRowsByKey = (rows = []) => new Map(
  (rows || [])
    .map((row) => [cpqRowKey(row), row])
    .filter(([key]) => key),
);

const cpqSourceRow = (action = {}, options = {}) => {
  const sourceKey = String(rowKeyCandidate(action) || rowKeyCandidate(action.sourceEvidence)).trim().toLowerCase();
  if (!sourceKey) return null;
  return options.cpqRowsByKey?.get(sourceKey) || null;
};

const cpqSourceIsNonBillable = (action = {}, options = {}) => {
  const explicit = booleanValue(action.sourceRowIsBillable ?? action.source_row_is_billable);
  if (explicit !== null) return explicit === false;
  const evidence = action.sourceEvidence || {};
  const evidenceBillable = booleanValue(evidence.isBillable ?? evidence.is_billable ?? evidence.row?.isBillable ?? evidence.row?.is_billable);
  if (evidenceBillable !== null) return evidenceBillable === false;
  const row = cpqSourceRow(action, options);
  const rowBillable = booleanValue(row?.isBillable ?? row?.is_billable);
  return rowBillable === false;
};

const cpqMutationUsesExistingRow = (action = {}) => (
  action.actionType !== VARIANCE_ACTIONS.ADD_TO_CPQ && action.compiledMutation !== 'add'
);

const sourceEvidencePrice = (action = {}) => {
  const evidence = action.sourceEvidence || {};
  return evidence.price
    ?? evidence.dealerCost
    ?? evidence.budget
    ?? evidence.mrr
    ?? evidence.dealerPrice
    ?? evidence.originalPrice;
};

const actionBasePrice = (action = {}) => toMoneyNumber(action.originalPrice ?? sourceEvidencePrice(action) ?? action.price ?? action.sourcePrice, 0);

const actionProposedPrice = (action = {}) => {
  if (
    action.actionType === VARIANCE_ACTIONS.CUSTOM
    && action.customPrice !== null
    && action.customPrice !== undefined
    && action.customPrice !== ''
  ) {
    return toMoneyNumber(action.customPrice, actionBasePrice(action));
  }
  if (action.proposedPrice !== null && action.proposedPrice !== undefined && action.proposedPrice !== '') {
    return toMoneyNumber(action.proposedPrice, actionBasePrice(action));
  }
  if (action.customPrice !== null && action.customPrice !== undefined && action.customPrice !== '') {
    return toMoneyNumber(action.customPrice, actionBasePrice(action));
  }
  return actionBasePrice(action);
};

const actionDeltaLabel = (action = {}) => {
  if (action.actionType === VARIANCE_ACTIONS.ADD_TO_GM || action.actionType === VARIANCE_ACTIONS.ADD_TO_CPQ) return 'Add product';
  if (action.actionType === VARIANCE_ACTIONS.REMOVE_FROM_GM || action.actionType === VARIANCE_ACTIONS.REMOVE_FROM_CPQ) return 'Remove product';
  if (action.actionType === VARIANCE_ACTIONS.REPLACE_PRODUCT) return 'Replace product';
  if (action.actionType === VARIANCE_ACTIONS.CHANGE_PRICE) return 'Change price';
  if (action.actionType === VARIANCE_ACTIONS.CUSTOM) return `Custom ${action.compiledMutation || 'adjust'}`;
  return action.actionLabel || action.actionType || 'Variance action';
};

export const varianceActionDeltaForFile = (action = {}, fileKind, options = {}) => {
  if (!fileKind || action.actionType === VARIANCE_ACTIONS.FORCE_EVIDENCE_MATCH) return 0;
  const targetFile = actionTargetFile(action);
  if (targetFile !== fileKind) return 0;
  if (fileKind === 'cpq' && cpqMutationUsesExistingRow(action) && cpqSourceIsNonBillable(action, options)) return 0;

  if (action.actionType === VARIANCE_ACTIONS.ADD_TO_GM || action.actionType === VARIANCE_ACTIONS.ADD_TO_CPQ) {
    return actionProposedPrice(action);
  }
  if (action.actionType === VARIANCE_ACTIONS.REMOVE_FROM_GM || action.actionType === VARIANCE_ACTIONS.REMOVE_FROM_CPQ) {
    return -actionBasePrice(action);
  }
  if (
    action.actionType === VARIANCE_ACTIONS.REPLACE_PRODUCT
    || action.actionType === VARIANCE_ACTIONS.CHANGE_PRICE
  ) {
    return actionProposedPrice(action) - actionBasePrice(action);
  }
  if (action.actionType === VARIANCE_ACTIONS.CUSTOM) {
    if (action.compiledMutation === 'add') return actionProposedPrice(action);
    if (action.compiledMutation === 'remove') return -actionBasePrice(action);
    return actionProposedPrice(action) - actionBasePrice(action);
  }
  return 0;
};

export const varianceActionMathKey = (action = {}) => {
  if (action.actionType === VARIANCE_ACTIONS.FORCE_EVIDENCE_MATCH) return null;
  if (action.actionType === VARIANCE_ACTIONS.ADD_TO_GM || action.actionType === VARIANCE_ACTIONS.ADD_TO_CPQ) return null;
  const fileKind = actionTargetFile(action);
  if (!fileKind) return null;
  const rowKey = rowKeyCandidate(action) || rowKeyCandidate(action.sourceEvidence);
  if (!rowKey) return null;
  return `${fileKind}:${String(rowKey).trim().toLowerCase()}`;
};

export const effectiveVarianceActionsForTotals = (actions = []) => {
  const seen = new Set();
  return actions.filter((action) => {
    const key = varianceActionMathKey(action);
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const totalDeltaRowsForFile = (actions = [], fileKind, options = {}) => {
  const deltaOptions = {
    ...options,
    cpqRowsByKey: cpqRowsByKey(options.cpqRows || []),
  };
  return effectiveVarianceActionsForTotals(actions)
  .map((action) => ({
    id: action.id,
    label: actionDeltaLabel(action),
    productCode: action.customProductCode || action.replacementProductCode || action.productCode || action.sourceProductCode || '--',
    status: action.saveStatus || 'draft',
    delta: varianceActionDeltaForFile(action, fileKind, deltaOptions),
  }))
  .filter((row) => changed(row.delta, 0));
};

export const applyVarianceActionTotals = (baseTotals = {}, actions = [], options = {}) => {
  const effectiveActions = effectiveVarianceActionsForTotals(actions);
  const deltaOptions = {
    ...options,
    cpqRowsByKey: cpqRowsByKey(options.cpqRows || []),
  };
  const gmOriginal = toMoneyNumber(baseTotals.gmTotal, 0);
  const cpqOriginal = toMoneyNumber(baseTotals.cpqTotal, 0);
  const dpeOriginal = toMoneyNumber(baseTotals.dpeTotal, 0);
  const gmDelta = effectiveActions.reduce((sum, action) => sum + varianceActionDeltaForFile(action, 'gm', deltaOptions), 0);
  const cpqDelta = effectiveActions.reduce((sum, action) => sum + varianceActionDeltaForFile(action, 'cpq', deltaOptions), 0);
  const gmTotal = toMoneyNumber(gmOriginal + gmDelta, 0);
  const cpqTotal = toMoneyNumber(cpqOriginal + cpqDelta, 0);
  const dpeTotal = toMoneyNumber(adjustedDpeTotalForStatus({
    baseTotal: dpeOriginal,
    dpeActions: options.dpeActions || [],
    activeStatus: options.dpeStatus || 'LIVE',
  }), dpeOriginal);
  const hasUnsavedForFile = (fileKind) => effectiveActions.some((action) => (
    !PERSISTED_STATUSES.has(action.saveStatus || action.status || 'draft')
    && changed(varianceActionDeltaForFile(action, fileKind, deltaOptions), 0)
  ));
  const statusFor = (original, adjusted, fileKind) => {
    if (!changed(original, adjusted)) return null;
    return hasUnsavedForFile(fileKind) ? 'draft' : 'saved';
  };

  return {
    gmTotal,
    cpqTotal,
    dpeTotal,
    originalTotals: {
      gmTotal: gmOriginal,
      cpqTotal: cpqOriginal,
      dpeTotal: dpeOriginal,
    },
    totalStatuses: {
      gm: statusFor(gmOriginal, gmTotal, 'gm'),
      cpq: statusFor(cpqOriginal, cpqTotal, 'cpq'),
      dpe: changed(dpeOriginal, dpeTotal) ? 'dpe_action_preview' : null,
    },
    deltas: {
      gmVsCpq: gmTotal - cpqTotal,
      gmVsDpe: gmTotal - dpeTotal,
      cpqVsDpe: cpqTotal - dpeTotal,
    },
  };
};
